<template>
  <div ref="element"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { debug as d } from "debug";
import { ForceGraph3DInstance } from "3d-force-graph";
import { Object3D, Raycaster, Vector3 } from "three";
import { CSS2DObject, CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

const debug = d("cs.heatmaps");

const props = defineProps<{
  graph: ForceGraph3DInstance;
  type: "Solo" | "Viewpoints";
  renderer: CSS2DRenderer;
  hoveredNode: any;
  enabled: boolean;
}>();
const element = ref<HTMLElement>();

const tooltipDiv = document.createElement("div");
tooltipDiv.className = "three-head-tooltip tooltip in";
tooltipDiv.textContent = "";

const tooltipObj = new CSS2DObject(tooltipDiv);
tooltipObj.position.set(0, 0, 0);
tooltipObj.name = "tooltip";
tooltipObj.visible = false;

const raycaster = new Raycaster();
const mouse = { x: 0, y: 0 };
let hoveredCircle: Object3D | null = null;

function onPointerMove(event) {
  if (props.enabled && props.hoveredNode) {
    // Check to see if we're over one of the circles
    const canvas = props.graph.renderer().domElement;
    const rect = canvas!.getBoundingClientRect();
    const x = event.clientX - rect!.left;
    const y = event.clientY - rect!.top;
    // debug(graph.value?.renderer().domElement);
    mouse.x = (x / canvas!.clientWidth) * 2 - 1;
    mouse.y = (y / canvas!.clientHeight) * -2 + 1;
    const camera = props.graph.camera();
    const scene = props.graph.scene();
    if (camera && scene) {
      raycaster.setFromCamera(mouse, camera);
      const circles =
        // eslint-disable-next-line no-underscore-dangle
        props.hoveredNode.__threeObj?.children[0].children.filter((o) =>
          o.name.startsWith("leader-circle"),
        ) || [];
      const matchingCircles = raycaster.intersectObjects(circles, true);
      if (matchingCircles.length > 0) {
        const match = matchingCircles[0].object.name.startsWith("leader-circle")
          ? matchingCircles[0].object
          : matchingCircles[0].object.parent;
        tooltipObj.visible = true;
        const newPosition = new Vector3();
        const { x: mx, y: my, z: mz } = match!.getWorldPosition(newPosition);
        tooltipObj.position.set(mx, my, mz);
        const { leaderType, value } = match!.userData;
        tooltipObj.element.innerText = `${leaderType}: ${value}% fit`;

        if (
          hoveredCircle !== match &&
          typeof hoveredCircle?.userData?.callbacks?.onHoverEnd === "function"
        ) {
          hoveredCircle?.userData?.callbacks?.onHoverEnd();
        }
        if (hoveredCircle !== match && typeof match?.userData?.callbacks?.onHover === "function") {
          match?.userData?.callbacks?.onHover();
        }

        hoveredCircle = match as Object3D;
        return;
      }
    }
  }
  if (tooltipObj.visible) tooltipObj.visible = false;
  if (hoveredCircle) {
    if (typeof hoveredCircle?.userData?.callbacks?.onHoverEnd === "function") {
      hoveredCircle?.userData?.callbacks?.onHoverEnd();
    }
    hoveredCircle = null;
  }
}

onMounted(() => {
  window.addEventListener("pointermove", onPointerMove);
  props.graph.scene().add(tooltipObj);
  const controls = new OrbitControls(props.graph.camera(), element.value);
  controls.minDistance = 5;
  controls.maxDistance = 100;
});

onUnmounted(() => {
  window.removeEventListener("pointermove", onPointerMove);
});
</script>

<style lang="less">
@import (reference) "../../../styles/bootstrap/variables.less";

.three-head-tooltip {
  background-color: black;
  padding: 5px;
  color: white;
  opacity: 1;
  margin-top: -25px;

  &::before {
    content: "";
    position: absolute;
    top: calc(100% - 1px);
    left: calc(50% - 10px);
    display: block;
    border-color: #000000 transparent transparent transparent;
    border-style: solid;
    border-width: 10px;
    width: 0;
    height: 0;
    z-index: 99;
  }
}
</style>
